import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import ReactSwipe from 'react-swipe';

import Icon from '../../components/Icon/Icon';
import ButtonT3 from '../../components/lib/ButtonT3/ButtonT3';
import {
  Icons as OnboardingIcons,
  Images as OnboardingImages,
  messages as OnboardingMessages,
  PurchaseLinks,
  SectionNames,
  StatsOnboardingActions,
} from '../../constants/Onboarding';
import { createBEM } from '../../lib/utils/css';
import mediaQueries from '../../sass/definitions/_media-queries.scss';
import Logger from '../../tools/Logger';

import GenomeTile from './GenomeTile';
import SectionCircle from './SectionCircle';

import './GenomeSection.scss';

const logger = Logger.child({ component: 'GenomeSection' });

export class GenomeSection extends React.Component {
  static propTypes = {
    isMobileDevice: PropTypes.bool,
    isIEorEdge: PropTypes.bool,
    appLink: PropTypes.string,
    appLinkCtaText: PropTypes.object,
    linkClicked: PropTypes.func.isRequired,
    appStoreClicked: PropTypes.func.isRequired,
  };

  state = {
    showGenomeCarousel: false,
    currentCarouselIndex: 0,
    areCarouselButtonsHidden: false,
  };

  componentWillMount() {
    this.genomeSectionRef = React.createRef();
  }

  componentDidMount = () => {
    this.setUpCarousel();
  };

  handleStoreClick = url => {
    this.props.appStoreClicked();
    switch (url) {
      case PurchaseLinks.ITUNES:
        this.props.linkClicked(StatsOnboardingActions.CLICK_IOS_STORE);
        break;
      case PurchaseLinks.GOOGLE:
        this.props.linkClicked(StatsOnboardingActions.CLICK_GOOGLE_STORE);
        break;
    }
    this.props.linkClicked(StatsOnboardingActions.CAROUSEL_APP);
    return true;
  };

  setUpCarousel = () => {
    if (!mediaQueries.MEDIA_QUERY_XS) {
      logger.error('xs media query not found. Carousel will not work');
      return;
    }
    const mediaQueryStr = mediaQueries.MEDIA_QUERY_XS.replace(/"/g, '');
    this._mediaQuery = window.matchMedia(mediaQueryStr);

    if (this._mediaQuery.matches) {
      this.setState({ showGenomeCarousel: true });
    }
    this._mediaQuery.addListener(this.handleMediaQueryChange);
  };

  handleMediaQueryChange = mediaQuery => {
    if (mediaQuery.matches) {
      this.setState({ showGenomeCarousel: true });
    } else {
      this.setState({ showGenomeCarousel: false });
    }
  };

  handleChange = carouselIndex => {
    this.setState({
      currentCarouselIndex: carouselIndex,
    });
  };

  renderDots = () => {
    const dots = [0, 1, 2].map(i => {
      const className = i === this.state.currentCarouselIndex ? 'dot-filled' : 'dot-unfilled';
      return <Icon className={className} glyph={OnboardingIcons.Dot} key={i} />;
    });

    return <div className='GenomeSection__dots'> {dots} </div>;
  };

  doCarouselTransition = direction => {
    // Hide buttons during the swipe transition and re-show them .5 sec later
    this.setState({
      areCarouselButtonsHidden: true,
    });

    this.carouselRef[direction]();

    setTimeout(() => {
      this.setState({
        areCarouselButtonsHidden: false,
      });
    }, 500);
  };

  handleNextClick = () => {
    this.doCarouselTransition('next');
  };

  handlePreviousClick = () => {
    this.doCarouselTransition('prev');
  };

  renderCarousel = () => {
    return (
      <ReactSwipe
        swipeOptions={{ callback: this.handleChange }}
        ref={el => (this.carouselRef = el)}
      >
        <GenomeTile
          isResponsive={true}
          isMobileDevice={this.props.isMobileDevice}
          image={OnboardingImages.MGP_GUITAR}
          tileText={OnboardingMessages.genomeTile1Text}
          tileHeader={OnboardingMessages.genomeTile1Header}
        />
        <GenomeTile
          isResponsive={true}
          isMobileDevice={this.props.isMobileDevice}
          image={OnboardingImages.MGP_HEADPHONES}
          tileText={OnboardingMessages.genomeTile2Text}
          tileHeader={OnboardingMessages.genomeTile2Header}
        />
        <GenomeTile
          isResponsive={true}
          isMobileDevice={this.props.isMobileDevice}
          image={OnboardingImages.MGP_PANDORA}
          tileText={OnboardingMessages.genomeTile3Text}
          tileHeader={OnboardingMessages.genomeTile3Header}
        />
      </ReactSwipe>
    );
  };

  renderCarouselNav = () => {
    const buttonClasses = createBEM('GenomeSection__carouselNav__button', {
      hidden: this.state.areCarouselButtonsHidden,
    });

    return (
      <div className='GenomeSection__carouselNav'>
        <div className='GenomeSection__carouselNav__inner'>
          <button className={buttonClasses} onClick={this.handlePreviousClick}>
            <Icon className='icon-back' glyph={OnboardingIcons.ChevronBack} />
          </button>
          <button className={buttonClasses} onClick={this.handleNextClick}>
            <Icon className='icon-back' glyph={OnboardingIcons.ChevronForward} />
          </button>
        </div>
      </div>
    );
  };

  renderGenomeSectionDesktop = () => {
    const tiles = this.state.showGenomeCarousel ? (
      this.renderCarousel()
    ) : (
      <React.Fragment>
        <GenomeTile
          isResponsive={true}
          image={OnboardingImages.MGP_GUITAR}
          tileText={OnboardingMessages.genomeTile1Text}
          tileHeader={OnboardingMessages.genomeTile1Header}
        />
        <GenomeTile
          isResponsive={true}
          image={OnboardingImages.MGP_HEADPHONES}
          tileText={OnboardingMessages.genomeTile2Text}
          tileHeader={OnboardingMessages.genomeTile2Header}
        />
        <GenomeTile
          isResponsive={true}
          image={OnboardingImages.MGP_PANDORA}
          tileText={OnboardingMessages.genomeTile3Text}
          tileHeader={OnboardingMessages.genomeTile3Header}
        />
      </React.Fragment>
    );

    return (
      <div
        ref={this.genomeSectionRef}
        className='GenomeSection GenomeSection--desktop GenomeSection--isResponsive'
      >
        {!this.props.isMobileDevice && (
          <SectionCircle
            type={SectionNames.GENOME}
            svg={OnboardingIcons.ThumbUpIcon}
            sectionRef={this.genomeSectionRef}
            isIEorEdge={this.props.isIEorEdge}
          />
        )}

        <div className='GenomeSection__circles'>
          <Icon className='circles' glyph={OnboardingIcons.LandingCirclesIcon} />
        </div>

        <div className='GenomeSection__title'>
          <FormattedMessage {...OnboardingMessages.genomeTitle} values={{ newline: <br /> }} />
        </div>

        {this.state.showGenomeCarousel && this.renderCarouselNav()}

        <div className='GenomeSection__tiles'>{tiles}</div>

        {this.state.showGenomeCarousel && this.renderDots()}
      </div>
    );
  };

  renderGenomeSectionMobile = () => {
    return (
      <div ref={this.genomeSectionRef} className='GenomeSection GenomeSection--isResponsive'>
        {!this.props.isMobileDevice && (
          <SectionCircle
            type={SectionNames.GENOME}
            svg={OnboardingIcons.ThumbUpIcon}
            sectionRef={this.genomeSectionRef}
          />
        )}

        <div className='GenomeSection__tiles GenomeSection__tiles--mobile'>
          {this.renderCarousel()}
        </div>

        {this.renderDots()}

        <ButtonT3
          href={this.props.appLink}
          size='large'
          isExternalLink={true}
          onClick={() => this.handleStoreClick(this.props.appLink)}
        >
          <FormattedMessage {...this.props.appLinkCtaText} />
        </ButtonT3>
      </div>
    );
  };

  render() {
    if (this.props.isMobileDevice && this.state.showGenomeCarousel) {
      return this.renderGenomeSectionMobile();
    }

    return this.renderGenomeSectionDesktop();
  }
}

export default GenomeSection;
